import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAccess } from 'contexts/access'
import { useUpgradable } from 'hooks/upgradable'
import { useLocationStateIntent, useLocationQueryIntent } from 'hooks/location-intent'
import { useOptionedString } from 'hooks/optioned-string'
import { Ghost } from 'components/button'
import ContextMenu from 'widgets/context-menu'
import { File as FileDocument } from 'styled-icons/feather'
import { SmartDocument } from 'components/feather'
import { Scrollable as Modal } from 'modals/generic'
import Smart from './smart'
import File from './file'

const AddDocument = ({
    addingFile, setAddingFile,
    addingSmart, setAddingSmart,
    preset = null,
    reset,
    salt,
    ...props
}) => {
    const { formatMessage } = useIntl()

    const { checkFeature } = useAccess()
    const checkUpgradable = useUpgradable()

    const smartDocumentsAvailable = checkFeature('smart-documents')
    const smartDocumentsUpgradable = checkUpgradable({ feature: 'smart-documents' })

    const optionizeString = useOptionedString()

    useLocationStateIntent({
        intent: 'add',
        action: data => setAddingSmart(data),
        requirements: [smartDocumentsAvailable]
    })

    useLocationQueryIntent({
        intent: 'add',
        action: () => setAddingSmart({}),
        requirements: [smartDocumentsAvailable]
    })

    const Interaction = addingSmart ?
        Smart :
        File

    const adding = !!addingFile || addingSmart

    salt = `${salt}:add`

    const addActions = {
        file: () => ({
            salt: `${salt}:file`,
            icon: <FileDocument size={24} />,
            label: formatMessage({
                id: 'documents_file_action_upload',
                defaultMessage: 'Upload a file'
            }),
            onClick: () => setAddingFile(true),
            effect: 'neutral'
        }),
        smart: () => {
            if(!smartDocumentsAvailable && !smartDocumentsUpgradable) {
                return null
            }

            return {
                salt: `${salt}:smart`,
                icon: <SmartDocument size={24} />,
                label: formatMessage({
                    id: 'documents_smart_document_action_add',
                    defaultMessage: 'Create a smart document'
                }),
                onClick: () => setAddingSmart({}),
                effect: 'neutral',
                ...(smartDocumentsUpgradable ? {
                    upgrade: {
                        feature: 'smart-documents'
                    }
                } : null)
            }
        }
    }

    return (
        <>
            {(!smartDocumentsAvailable && !smartDocumentsUpgradable) && (
                <Ghost
                    className="constructive"
                    onClick={() => setAddingFile(true)}>
                    <FormattedMessage
                        id="documents_file_action_upload"
                        defaultMessage="Upload a file" />
                </Ghost>
            )}
            {(smartDocumentsAvailable || smartDocumentsUpgradable) && (
                <ContextMenu
                    salt={`${salt}:add`}
                    actions={addActions}
                    trigger={(
                        <Ghost
                            className="constructive"
                            disabled={!!addingSmart}>
                            {optionizeString(formatMessage({
                                id: 'documents_action_add',
                                defaultMessage: 'Add document'
                            }))}
                        </Ghost>
                    )} />
            )}
            <Modal
                show={!!adding}
                dismiss={() => {
                    setAddingFile(false)
                    setAddingSmart(null)
                    reset()
                }}
                salt={salt}>
                <Interaction
                    {...props}
                    {...addingSmart}
                    {...preset}
                    salt={`${salt}:${!!addingFile ? 'file' : 'smart'}`} />
            </Modal>
        </>
    )
}

export default AddDocument